import { Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { IAMRoleOut } from '@repo/api-codegen';
import {
	apiQueryKey,
	fetchApiGetIamRoleUsers,
	useApiCloneIamRole,
	useApiDeleteIamRole,
} from '@repo/api-codegen';
import { UserRole } from '@repo/common/enums/UserRole';
import { Icon, IconButton, Text } from '@repo/foundations';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { queryClient } from '../../../../api';
import { useCheckIAMPermission } from '../../../../utils/authorization/roles';
import { useModals } from '../../../ModalManager/hooks';

interface IamRoleActionCellProps {
	iamRole: IAMRoleOut;
}

export function IamRoleActionCell({ iamRole }: IamRoleActionCellProps) {
	const navigate = useNavigate();
	const modals = useModals();
	const { hasPermission: canEditRole } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Roles.Update',
		defaultResult: false,
	});
	const { hasPermission: canDeleteRole } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Roles.Delete',
		defaultResult: false,
	});
	const { hasPermission: canCreateRole } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Roles.Create',
		defaultResult: false,
	});

	const { mutateAsync: deleteIamRole } = useApiDeleteIamRole({
		onSuccess: () => {
			notifications.show({
				title: 'Role deleted',
				message: 'Successfully deleted role',
				color: 'green',
			});
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('iam/iam_roles/iam_role'),
			});
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('iam/iam_roles/users'),
			});
		},
		onError: (error) => {
			notifications.show({
				title: 'Unable to delete role',
				message: error?.detail || 'Unknown error',
				color: 'red',
			});
		},
	});

	const { mutateAsync: cloneIamRole } = useApiCloneIamRole({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('iam/iam_roles/iam_role'),
			});
		},
	});

	const handleDelete = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();

			const usersAssignedToRole = await fetchApiGetIamRoleUsers({
				pathParams: {
					iamRoleId: iamRole.id,
				},
			});

			const existingUsersAssignedToRoleCount = usersAssignedToRole.filter(
				(user) => !user.disabled && !user.is_service_account
			).length;

			if (existingUsersAssignedToRoleCount > 0) {
				modals.openConfirmModal({
					title: 'Cannot delete role',
					children: (
						<Text size="sm">
							{`${existingUsersAssignedToRoleCount} ${
								existingUsersAssignedToRoleCount === 1 ? 'user' : 'users'
							} must be re-assigned to a different role before deleting this role.`}
						</Text>
					),
					confirmLabel: 'OK',
					cancelLabel: '',
				});
				return;
			}

			await deleteIamRole({
				pathParams: { iamRoleId: iamRole.id },
			});
		},
		[deleteIamRole, iamRole.id, modals]
	);

	const handleEdit = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			navigate(`/roles/edit/${iamRole.id}`);
		},
		[iamRole.id, navigate]
	);

	const handleClone = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			await cloneIamRole({
				pathParams: { iamRoleId: iamRole.id },
			});
		},
		[iamRole.id, cloneIamRole]
	);

	return (
		<Menu position="bottom-end" withinPortal>
			<Menu.Target>
				<IconButton
					iconName="dots"
					variant="tertiary"
					aria-label="row-actions"
					onClick={(e) => e.stopPropagation()}
				/>
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Item
					icon={
						<Icon
							name={iamRole.is_legacy || !canEditRole ? 'eye' : 'edit'}
							size="md"
						/>
					}
					onClick={handleEdit}
				>
					<Text size="sm">
						{iamRole.is_legacy || !canEditRole ? 'View' : 'Edit role'}
					</Text>
				</Menu.Item>
				{canCreateRole && (
					<Menu.Item
						icon={<Icon name="copy" size="md" />}
						onClick={handleClone}
					>
						<Text size="sm">Duplicate</Text>
					</Menu.Item>
				)}
				{!iamRole.is_legacy && canDeleteRole && (
					<Menu.Item
						icon={<Icon name="trash" size="md" />}
						onClick={handleDelete}
					>
						<Text size="sm">Delete</Text>
					</Menu.Item>
				)}
			</Menu.Dropdown>
		</Menu>
	);
}
